import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface Provider {
  id: string;
  name: string;
  models: { id: string; name: string }[];
  api_url: string;
}

interface Props {
  API: string;
}

export default function AISettings({ API }: Props) {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [appSettings, setAppSettings] = useState<any>({});
  const [selectedProvider, setSelectedProvider] = useState('anthropic');
  const [selectedModel, setSelectedModel] = useState('claude-sonnet-5-5');
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [defaultSalutation, setDefaultSalutation] = useState('Panie');

  useEffect(() => {
    loadAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    try {
      const [prov, settings] = await Promise.all([
        axios.get(API + '/settings/ai/providers'),
        axios.get(API + '/settings/app')
      ]);
      setProviders(prov.data.providers);
      const s = settings.data;
      setAppSettings(s);
      if (s.ai_provider) setSelectedProvider(s.ai_provider);
      if (s.ai_model) setSelectedModel(s.ai_model);
      if (s.ai_api_key) setApiKey(s.ai_api_key);
      if (s.default_salutation) setDefaultSalutation(s.default_salutation);
    } catch (e) {
      setStatus('Blad ladowania ustawien');
    }
  };

  const currentProvider = providers.find(p => p.id === selectedProvider);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await Promise.all([
        axios.post(API + '/settings/app/ai_provider', { value: selectedProvider }),
        axios.post(API + '/settings/app/ai_model', { value: selectedModel }),
        axios.post(API + '/settings/app/ai_api_key', { value: apiKey }),
        axios.post(API + '/settings/app/default_salutation', { value: defaultSalutation }),
      ]);
      setStatus('Zapisano ustawienia AI');
      setTimeout(() => setStatus(''), 3000);
    } catch (e) {
      setStatus('Blad zapisu');
    }
    setSaving(false);
  };

  const testConnection = async () => {
    if (!apiKey) { setStatus('Wpisz klucz API'); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(API + '/settings/ai/test-connection', {
        provider: selectedProvider,
        api_key: apiKey,
        model: selectedModel
      });
      setTestResult(res.data);
    } catch (e: any) {
      setTestResult({ status: 'error', error: e.response?.data?.detail || e.message });
    }
    setTesting(false);
  };

  const s: any = {
    card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 12 },
    input: { border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' as const, outline: 'none' },
    btn: { border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 },
    label: { fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: 0.5, display: 'block', marginBottom: 4, marginTop: 12 },
  };

  const PROVIDER_COLORS: any = {
    anthropic: { bg: '#fdf4ff', border: '#e9d5ff', badge: '#7c3aed', text: '#581c87' },
    openai: { bg: '#f0fdf4', border: '#bbf7d0', badge: '#15803d', text: '#14532d' },
    gemini: { bg: '#eff6ff', border: '#bfdbfe', badge: '#1d4ed8', text: '#1e3a8a' },
    mistral: { bg: '#fff7ed', border: '#fed7aa', badge: '#c2410c', text: '#7c2d12' },
    kimi: { bg: '#f0f9ff', border: '#bae6fd', badge: '#0369a1', text: '#0c4a6e' },
  };

  const colors = PROVIDER_COLORS[selectedProvider] || PROVIDER_COLORS.anthropic;

  return (
    <div>
      <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Konfiguracja AI</h2>

      {/* Wybor providera */}
      <div style={s.card}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Wybierz dostawce AI</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
          {providers.map(p => {
            const c = PROVIDER_COLORS[p.id] || PROVIDER_COLORS.anthropic;
            const isActive = selectedProvider === p.id;
            return (
              <button key={p.id} onClick={() => {
                setSelectedProvider(p.id);
                setSelectedModel(p.models[0]?.id || '');
                setTestResult(null);
              }} style={{
                ...s.btn,
                background: isActive ? c.bg : '#f9fafb',
                border: isActive ? '2px solid ' + c.badge : '2px solid #e5e7eb',
                color: isActive ? c.text : '#6b7280',
                padding: '10px 16px',
                fontSize: 13,
                fontWeight: isActive ? 700 : 500,
              }}>
                {p.name}
                {appSettings.ai_provider === p.id && (
                  <span style={{ marginLeft: 6, fontSize: 10, background: c.badge, color: '#fff', borderRadius: 10, padding: '1px 6px' }}>AKTYWNY</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Konfiguracja wybranego providera */}
      {currentProvider && (
        <div style={{ ...s.card, border: '2px solid ' + colors.border, background: colors.bg }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: colors.text }}>{currentProvider.name}</div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                Klucz API: <a href={currentProvider.api_url} target="_blank" rel="noreferrer"
                  style={{ color: colors.badge }}>{currentProvider.api_url}</a>
              </div>
            </div>
            <span style={{ fontSize: 11, background: colors.badge, color: '#fff', borderRadius: 12, padding: '3px 10px' }}>
              {selectedProvider.toUpperCase()}
            </span>
          </div>

          <label style={s.label}>Model</label>
          <select value={selectedModel} onChange={e => setSelectedModel(e.target.value)}
            style={{ ...s.input, marginBottom: 4 }}>
            {currentProvider.models.map(m => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>

          <label style={s.label}>Klucz API</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              type={showKey ? 'text' : 'password'}
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              placeholder="Wklej klucz API..."
              style={{ ...s.input, fontFamily: showKey ? 'monospace' : 'inherit', fontSize: showKey ? 11 : 13 }}
            />
            <button onClick={() => setShowKey(!showKey)}
              style={{ ...s.btn, background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', flexShrink: 0 }}>
              {showKey ? 'Ukryj' : 'Pokaz'}
            </button>
          </div>

          <label style={s.label}>Domyślna forma grzecznościowa</label>
          <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>Używana gdy AI nie może określić płci klienta na podstawie treści wiadomości.</div>
          <select value={defaultSalutation} onChange={e => setDefaultSalutation(e.target.value)} style={{ ...s.input, marginBottom: 4 }}>
            <option value="Panie">Panie (domyślnie męska)</option>
            <option value="Pani">Pani (domyślnie żeńska)</option>
            <option value="Pani/Panie">Pani/Panie (neutralna)</option>
          </select>

          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' as const }}>
            <button onClick={testConnection} disabled={testing}
              style={{ ...s.btn, background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' }}>
              {testing ? 'Testuje...' : 'Testuj polaczenie'}
            </button>
            <button onClick={saveSettings} disabled={saving}
              style={{ ...s.btn, background: colors.badge, color: '#fff' }}>
              {saving ? 'Zapisuje...' : 'Zapisz i aktywuj'}
            </button>
            {(appSettings.ai_api_key || (appSettings.ai_provider && appSettings.ai_provider !== 'anthropic')) && (
              <button onClick={async () => {
                if (!window.confirm('Usunąć własny klucz API i wrócić do domyślnego AI OPENkontakt?')) return;
                setSaving(true);
                try {
                  await Promise.all([
                    axios.post(API + '/settings/app/ai_api_key', { value: '' }),
                    axios.post(API + '/settings/app/ai_provider', { value: 'anthropic' }),
                    axios.post(API + '/settings/app/ai_model', { value: 'claude-haiku-5-5' }),
                  ]);
                  setApiKey('');
                  setSelectedProvider('anthropic');
                  setSelectedModel('claude-haiku-5-5');
                  setStatus('Wrócono do domyślnego AI OPENkontakt');
                  setTimeout(() => setStatus(''), 3000);
                  loadAll();
                } catch (e) { setStatus('Błąd'); }
                setSaving(false);
              }} disabled={saving}
                style={{ ...s.btn, background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5' }}>
                ✕ Usuń własny klucz — wróć do domyślnego AI
              </button>
            )}
          </div>

          {testResult && (
            <div style={{
              marginTop: 12, padding: 12, borderRadius: 6,
              background: testResult.status === 'ok' ? '#f0fdf4' : '#fef2f2',
              border: '1px solid ' + (testResult.status === 'ok' ? '#bbf7d0' : '#fecaca')
            }}>
              {testResult.status === 'ok' ? (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#15803d' }}>Polaczenie dziala!</div>
                  <div style={{ fontSize: 11, color: '#166534', marginTop: 4 }}>Odpowiedz: {testResult.response}</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#dc2626' }}>Blad polaczenia</div>
                  <div style={{ fontSize: 11, color: '#991b1b', marginTop: 4 }}>{testResult.error}</div>
                </div>
              )}
            </div>
          )}

          {status && (
            <div style={{ marginTop: 10, fontSize: 12, color: '#15803d', fontWeight: 500 }}>{status}</div>
          )}
        </div>
      )}

      {/* Info */}
      <div style={{ ...s.card, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Jak to dziala?</div>
        <div style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.6 }}>
          Wybrany dostawca AI bedzie uzywany do generowania draftow odpowiedzi dla agentow.
          Klasyfikacja wiadomosci (kategoria, jezyk, priorytet) dziala nadal na Claude Haiku dla zachowania stabilnosci.
          Jezeli wybrany provider bedzie niedostepny, aplikacja automatycznie uzyje Claude jako zapasowego.
          Zmiana providera nie wplywa na baze wiedzy - jest ona niezalezna od AI.
        </div>
      </div>
    </div>
  );
}
